import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Grid,
  Typography,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Sort as SortIcon,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { setSearchResults } from '../../redux/slices/searchSlice';

const getTypeColor = (type) => {
  switch (type) {
    case 'article':
      return 'primary';
    case 'guide':
      return 'success';
    case 'faq':
      return 'warning';
    case 'tutorial':
      return 'info';
    default:
      return 'default';
  }
};

const KnowledgeBaseList = ({ items, onSelect, searchTerm = '' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const { results } = useSelector((state) => state.search);

  useEffect(() => {
    if (searchTerm) {
      const filteredItems = items.filter((item) => {
        const searchLower = searchTerm.toLowerCase();
        const itemName = item.name?.toLowerCase() || '';
        const itemDescription = item.description?.toLowerCase() || '';
        const itemContent = item.content?.toLowerCase() || '';

        return (
          itemName.includes(searchLower) ||
          itemDescription.includes(searchLower) ||
          itemContent.includes(searchLower)
        );
      });
      dispatch(
        setSearchResults({ module: 'knowledgeBase', results: filteredItems })
      );
    } else {
      dispatch(setSearchResults({ module: 'knowledgeBase', results: items }));
    }
  }, [searchTerm, items, dispatch]);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortItems = (items) => {
    if (!sortField) return items;

    return [...items].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'collection':
          comparison = a.collection.localeCompare(b.collection);
          break;
        default:
          return 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const displayedItems = useMemo(() => {
    if (!searchTerm) return items;

    return items.filter((item) => {
      const searchLower = searchTerm.toLowerCase();
      const itemName = item.name?.toLowerCase() || '';
      const itemDescription = item.description?.toLowerCase() || '';
      const itemContent = item.content?.toLowerCase() || '';

      return (
        itemName.includes(searchLower) ||
        itemDescription.includes(searchLower) ||
        itemContent.includes(searchLower)
      );
    });
  }, [items, searchTerm]);

  const sortedItems = sortItems(displayedItems);

  const SortableHeader = ({ field, children }) => (
    <TableCell
      onClick={() => handleSort(field)}
      sx={{
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.hover' },
        whiteSpace: 'nowrap',
        position: 'relative',
        paddingRight: '24px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {children}
        {sortField === field && (
          <Box sx={{ position: 'absolute', right: 4 }}>
            {sortDirection === 'asc' ? (
              <ArrowUpward fontSize="small" />
            ) : (
              <ArrowDownward fontSize="small" />
            )}
          </Box>
        )}
      </Box>
    </TableCell>
  );

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <SortableHeader field="name">
              {t('knowledgeBase.name')}
            </SortableHeader>
            <SortableHeader field="type">
              {t('knowledgeBase.type')}
            </SortableHeader>
            <SortableHeader field="collection">
              {t('knowledgeBase.collection')}
            </SortableHeader>
            <TableCell>{t('knowledgeBase.match')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.map((item) => (
            <TableRow
              key={item.id}
              hover
              onClick={() => onSelect(item)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>{item.name}</TableCell>
              <TableCell>
                <Chip
                  label={t(`knowledgeBase.types.${item.type}`)}
                  color={getTypeColor(item.type)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {t(`knowledgeBase.collections.${item.collection}`)}
              </TableCell>
              <TableCell>{t(`knowledgeBase.matches.${item.match}`)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default KnowledgeBaseList;
